import { readExperimentalFeatureEnabled } from "@istari-frontend/experimental-features";

import type { BranchId } from "~/branching/model/branch";

export const routes = {
  Root: "/",
  SignIn: "/sign-in",
  Systems: "/systems",
  SystemsDashboard: "/systems-dashboard",
  AiChat: "/ai-chat",
  SystemHome: "/systems-dashboard/:systemId",
  SystemHomeDirectory: "directory/:nodeId?",
  SystemHomeWorkflows: "workflows",
  SystemHomeBranches: "branches",
  SystemHomeSettings: "settings",
  SystemHomeCommits: "commits",
  SystemHomeChangesAvailable: "changes-available/:nodeId?",
  SystemHomeCommitDetail: ":commitId/:nodeId?",
  SystemHomeChangeRequests: "change-requests",
  SystemHomeChangeRequestDetail: "change-requests/:id",
  SystemHomeCreateChangeRequest: "change-requests/new",
  SystemHomeBranchScope: "bid/:branchId",
  Resources: "/resources",
  Libraries: "/libraries",
  LibraryDetails: "/libraries/:libraryId",
  PackageDetails: "/libraries/:libraryId/packages/:packageId",
  Package: "/packages/:packageId",
  ResourcesDashboard: "/resources-dashboard",
  ResourcesDrive: "/resources-drive",
  Jobs: "/jobs",
  JobDetails: "/jobs/:jobId",
  Admin: "/admin",
  Settings: "/settings",
  SettingsProfile: "/settings/profile",
  SettingsPassword: "/settings/password",
  SettingsNotifications: "/settings/notifications",
  SettingsApplication: "/settings/application",
  SettingsDeveloper: "/settings/developer",
  SettingsAiInstructions: "/settings/ai-instructions",
  SettingsLinkedAccounts: "/settings/linked-accounts",
  Comment: "/comment",
  FunctionAuthCallback: "/callback",
  NotFound: "/404",
  Documentation: "/documentation",
  MagicDocs: "/magic-docs",
  Upload: "/upload",
  AdminUsers: "/admin/users",
  AdminUser: "/admin/users/:userId",
  Console: "/console",
  ConsoleTenants: "/console/tenants",
  ConsoleTenant: "/console/tenants/:tenantId",
  ConsoleTenantUsers: "/console/tenants/:tenantId/users",
  ConsoleUsers: "/console/users",
  ConsoleUser: "/console/users/:userId",
  AdminFunctions: "/admin/functions",
  AdminControlTags: "/admin/control-tags",
  Agents: "/agents",
  Notifications: "/notifications",
  AdminAppIntegrations: "/admin/app-integrations",
  AdminIntegrations: "/admin/integrations",
  AdminEncryptionKeys: "/admin/encryption-keys",
  AdminInfosecLevels: "/admin/infosec-levels",
  AdminInfosec: "/admin/infosec",
  AdminUsage: "/admin/usage",
  AdminCompatibility: "/admin/compatibility",
  AdminAi: "/admin/ai",
  /** Retired pages, redirected to the tabs of `AdminAi`. */
  AdminAiSettings: "/admin/ai-settings",
  AdminAiModels: "/admin/ai-models",
  AdminSecureConnections: "/admin/secure-connections",
  AdminSecureConnectionSettings: "/admin/secure-connections/:kind/:connectionId",
  AdminSecureConnectionSyncStatus: "/admin/secure-connections/:kind/:connectionId/sync-status",
  CreateChangeRequest: "/systems/:systemId/change-requests/new",
  ChangeRequestDetail: "/systems/:systemId/change-requests/:id",
  /**
   * `/systems/{systemId}/…` — branch path, `…/history`, `…/history/{shortId}`, and legacy
   * `configId/snapshotId/…` sit in a single `*` (see `parseSystemRouteTail` in `System/`).
   */
  SystemDetails: "/systems/:systemId/*",
  Artifact: "/artifact/:artifactId/:artifactVersionId?",
  ResourcesUnparentedArtifact: "/resources/artifact/:artifactId/:artifactRevisionId?",
  ResourcesDriveUnparentedArtifact: "/resources-drive/artifact/:artifactId/:artifactRevisionId?",
  /**
   * Connected resources live in their own id space — a `ConnectedResourceDto` exposes no resource
   * id — so they need a segment of their own rather than sharing `/files/:modelId`. The revision
   * segment carries the version being viewed, the same way the resource routes do; without it the
   * versions list has nowhere to put a choice and every row reads as the latest.
   */
  FilesConnectedResource: "/files/connected/:connectedResourceId/:connectedRevisionId?",
  CommentDetails: "/comment/:commentId",
  Search: "/search",
} as const;

export const buildSystemWorkflowLogRoute = (systemId: string, entryId?: string, branchId?: BranchId | null) => {
  const branchPrefix = branchId ? `bid/${encodeURIComponent(branchId)}/` : "";
  return `/systems-dashboard/${systemId}/${branchPrefix}workflows${entryId ? `?entry=${entryId}` : ""}`;
};

export type ResourcePathTarget =
  | { resourceId: string; revisionId?: string }
  | { artifactId: string; artifactRevisionId?: string }
  | { resourceId: string; revisionId?: string; artifactId: string; artifactRevisionId?: string };

export function buildResourcePath(): string;
export function buildResourcePath(target: ResourcePathTarget): string;
export function buildResourcePath(target?: ResourcePathTarget): string {
  return buildResourcePathAtBase(routes.Resources, target);
}

export function buildResourcePathAtBase(base: string, target?: ResourcePathTarget): string {
  if (!target) return base;

  if ("artifactId" in target && !("resourceId" in target)) {
    const artifactRoot = `${base}/artifact`;
    const artifactBase = `${artifactRoot}/${target.artifactId}`;
    return target.artifactRevisionId ? `${artifactBase}/${target.artifactRevisionId}` : artifactBase;
  }

  if ("resourceId" in target && "artifactId" in target) {
    const parent = target.revisionId ? `${target.resourceId}/${target.revisionId}` : target.resourceId;
    const artifact = target.artifactRevisionId
      ? `${target.artifactId}/${target.artifactRevisionId}`
      : target.artifactId;
    return `${base}/${parent}/artifact/${artifact}`;
  }

  return target.revisionId ? `${base}/${target.resourceId}/${target.revisionId}` : `${base}/${target.resourceId}`;
}

export function buildResourceDrivePath(): string;
export function buildResourceDrivePath(target: ResourcePathTarget): string;
export function buildResourceDrivePath(target?: ResourcePathTarget): string {
  return buildResourcePathAtBase(routes.ResourcesDrive, target);
}

export function isResourceRedesignNavigationEnabled(): boolean {
  return readExperimentalFeatureEnabled("resource-redesign");
}

export function buildResourceNavigationPath(): string;
export function buildResourceNavigationPath(target: ResourcePathTarget): string;
export function buildResourceNavigationPath(target?: ResourcePathTarget): string {
  if (isResourceRedesignNavigationEnabled()) {
    return target === undefined ? buildResourceDrivePath() : buildResourceDrivePath(target);
  }
  return target === undefined ? buildResourcePath() : buildResourcePath(target);
}

export function isLegacyResourceLibraryPath(pathname: string): boolean {
  if (pathname === routes.Resources || pathname.startsWith(`${routes.Resources}/`)) {
    return true;
  }
  return false;
}

export function toResourceNavigationPath(pathname: string): string {
  if (!isResourceRedesignNavigationEnabled()) {
    return pathname;
  }
  if (isLegacyResourceLibraryPath(pathname)) {
    return pathname.replace(routes.Resources, routes.ResourcesDrive);
  }
  return pathname;
}

export type RouteKeys = keyof typeof routes;
export type RouteValues = (typeof routes)[keyof typeof routes];
